import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@altimateai/lego";
import { createDatabricksConnection, testDatabricksConnection } from "@/helpers/apis";
import {
  DatabricksAuthType,
  type DatabricksConnectionCreateRequest,
  type DatabricksConnectionTestRequest,
} from "@/pages/datastores/databricksTypes";
import type { DatabricksFormData } from "./index";

// Strip leading/trailing whitespace before sending to the API. Pasting
// from docs/wikis frequently picks up a space the user can't see, and the
// BE's strict regex on server_hostname/http_path then rejects with 422.
const trim = (v: string | undefined): string => (v ?? "").trim();

/**
 * Builds the auth-specific request data from form data.
 * Form validation ensures auth_type is PAT, OAUTH_M2M, or AZURE_SP, never empty.
 */
const buildAuthRequestData = (data: DatabricksFormData): DatabricksConnectionTestRequest => {
  const baseData = {
    server_hostname: trim(data.server_hostname),
    http_path: trim(data.http_path),
  };

  if (data.auth_type === DatabricksAuthType.AZURE_SP) {
    return {
      ...baseData,
      auth_type: data.auth_type,
      azure_tenant_id: trim(data.azure_tenant_id),
      client_id: trim(data.client_id),
      client_secret: trim(data.client_secret),
    };
  }

  if (data.auth_type === DatabricksAuthType.OAUTH_M2M) {
    return {
      ...baseData,
      auth_type: data.auth_type,
      client_id: trim(data.client_id),
      client_secret: trim(data.client_secret),
    };
  }

  return {
    ...baseData,
    auth_type: DatabricksAuthType.PAT,
    access_token: trim(data.access_token),
  };
};

/**
 * Builds the full create request data from form data.
 *
 * Optional fields use conditional spread so empty values are omitted from
 * the request body entirely (PR #2155 review F9) rather than sent as
 * `undefined`/`""` and forcing the BE to special-case them. All string
 * fields are also trimmed of leading/trailing whitespace.
 */
const buildCreateRequestData = (data: DatabricksFormData): DatabricksConnectionCreateRequest => {
  const authData = buildAuthRequestData(data);
  const description = trim(data.description);
  return {
    ...authData,
    name: trim(data.name),
    ...(description ? { description } : {}),
  };
};

export const useDatabricksConnection = () => {
  const navigate = useNavigate();

  const {
    mutate: testConnection,
    isPending: isTestingConnection,
    isSuccess: isTestSuccess,
    data: testResult,
    reset: resetTestResult,
  } = useMutation({
    mutationFn: async (data: DatabricksFormData) => {
      const response = await testDatabricksConnection(buildAuthRequestData(data));
      if (!response?.ok) throw new Error(response?.message || "Connection test failed");
      return response;
    },
    onSuccess: response => {
      toast({
        title: "Connection validated",
        description: response?.catalogs?.length
          ? `Successfully connected. Found ${response.catalogs.length} catalog(s).`
          : "Successfully connected to Databricks SQL Warehouse.",
      });
    },
    onError: (error: Error) => {
      // Surface the BE-classified message (e.g. "Invalid credentials.",
      // "Invalid server hostname.") rather than a generic catch-all.
      // The BE already user-facing-friendly-classifies the underlying
      // driver error in DatabricksConnectionService._classify_and_raise.
      toast({
        variant: "destructive",
        title: "Connection test failed",
        description: error.message || "Please check your connection details and try again.",
      });
    },
  });

  const { mutate: createConnection, isPending: isCreating } = useMutation({
    mutationFn: (data: DatabricksFormData) =>
      createDatabricksConnection(buildCreateRequestData(data)),
    onSuccess: () => {
      toast({
        title: "Connection created",
        description: "Databricks connection created successfully.",
      });
      navigate("/settings/datastores?refresh=true");
    },
    onError: (err: Error) => {
      toast({ variant: "destructive", title: "Error", description: err.message });
    },
  });

  return {
    testConnection,
    isTestingConnection,
    isTestSuccess,
    testResult,
    resetTestResult,
    createConnection,
    isCreating,
  };
};
